import type { MetricPanelDef } from './types';

/**
 * PromQL query templates per exporter type.
 *
 * Placeholders:
 *   {{instance}} — replaced at runtime with `device.host`
 *
 * Rate intervals are period-dependent and injected by useDeviceMetrics:
 *   1h -> 15s,  6h -> 1m,  24h -> 5m,  7d -> 30m
 * The templates use `$__rate_interval` which the hook replaces with the
 * correct interval before sending to Prometheus.
 */

// ---------------------------------------------------------------------------
// Node Exporter (Linux)
// ---------------------------------------------------------------------------
const nodeExporterPanels: MetricPanelDef[] = [
  // --- Stats ---
  {
    title: 'CPU Usage',
    group: 'Overview',
    chartType: 'stat',
    unit: 'percent',
    queries: [
      {
        expr: '100 - (avg by(instance) (rate(node_cpu_seconds_total{mode="idle",instance=~"{{instance}}.*"}[$__rate_interval])) * 100)',
        legend: 'CPU %',
      },
    ],
    thresholds: [
      { value: 90, color: '#d32f2f' },
      { value: 70, color: '#ed6c02' },
    ],
  },
  {
    title: 'Memory Usage',
    group: 'Overview',
    chartType: 'stat',
    unit: 'percent',
    queries: [
      {
        expr: '100 - ((node_memory_MemAvailable_bytes{instance=~"{{instance}}.*"} / node_memory_MemTotal_bytes{instance=~"{{instance}}.*"}) * 100)',
        legend: 'Memory %',
      },
    ],
    thresholds: [
      { value: 90, color: '#d32f2f' },
      { value: 70, color: '#ed6c02' },
    ],
  },
  {
    title: 'Disk Usage /',
    group: 'Overview',
    chartType: 'stat',
    unit: 'percent',
    queries: [
      {
        expr: '100 - ((node_filesystem_avail_bytes{instance=~"{{instance}}.*",mountpoint="/",fstype!="rootfs"} / node_filesystem_size_bytes{instance=~"{{instance}}.*",mountpoint="/",fstype!="rootfs"}) * 100)',
        legend: 'Disk %',
      },
    ],
    thresholds: [
      { value: 90, color: '#d32f2f' },
      { value: 80, color: '#ed6c02' },
    ],
  },
  {
    title: 'Load Average (1m)',
    group: 'Overview',
    chartType: 'stat',
    unit: 'none',
    queries: [
      {
        expr: 'node_load1{instance=~"{{instance}}.*"}',
        legend: 'Load 1m',
      },
    ],
  },

  // --- Time Series ---
  {
    title: 'CPU Usage',
    group: 'CPU',
    chartType: 'area',
    unit: 'percent',
    queries: [
      {
        expr: '100 - (avg by(instance) (rate(node_cpu_seconds_total{mode="idle",instance=~"{{instance}}.*"}[$__rate_interval])) * 100)',
        legend: 'CPU %',
      },
    ],
  },
  {
    title: 'Memory Usage',
    group: 'Memory',
    chartType: 'stacked-area',
    unit: 'bytes',
    queries: [
      {
        expr: 'node_memory_MemTotal_bytes{instance=~"{{instance}}.*"} - node_memory_MemAvailable_bytes{instance=~"{{instance}}.*"}',
        legend: 'Used',
      },
      {
        expr: 'node_memory_MemAvailable_bytes{instance=~"{{instance}}.*"}',
        legend: 'Available',
      },
    ],
  },
  {
    title: 'Disk Usage',
    group: 'Disk',
    chartType: 'area',
    unit: 'percent',
    queries: [
      {
        expr: '100 - ((node_filesystem_avail_bytes{instance=~"{{instance}}.*",fstype!~"tmpfs|overlay",mountpoint!~"/boot.*"} / node_filesystem_size_bytes{instance=~"{{instance}}.*",fstype!~"tmpfs|overlay",mountpoint!~"/boot.*"}) * 100)',
        legend: '{{mountpoint}}',
      },
    ],
  },
  {
    title: 'Disk I/O',
    group: 'Disk',
    chartType: 'area',
    unit: 'bytes/s',
    queries: [
      {
        expr: 'rate(node_disk_read_bytes_total{instance=~"{{instance}}.*"}[$__rate_interval])',
        legend: 'Read',
      },
      {
        expr: 'rate(node_disk_written_bytes_total{instance=~"{{instance}}.*"}[$__rate_interval])',
        legend: 'Write',
      },
    ],
  },
  {
    title: 'Network Traffic',
    group: 'Network',
    chartType: 'area',
    unit: 'bytes/s',
    queries: [
      {
        expr: 'rate(node_network_receive_bytes_total{instance=~"{{instance}}.*",device!~"lo|veth.*|docker.*|br-.*"}[$__rate_interval])',
        legend: 'Receive',
      },
      {
        expr: 'rate(node_network_transmit_bytes_total{instance=~"{{instance}}.*",device!~"lo|veth.*|docker.*|br-.*"}[$__rate_interval])',
        legend: 'Transmit',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Windows Exporter
// ---------------------------------------------------------------------------
const windowsExporterPanels: MetricPanelDef[] = [
  // --- Stats ---
  {
    title: 'CPU Usage',
    group: 'Overview',
    chartType: 'stat',
    unit: 'percent',
    queries: [
      {
        expr: '100 - (avg by(instance) (rate(windows_cpu_time_total{mode="idle",instance=~"{{instance}}.*"}[$__rate_interval])) * 100)',
        legend: 'CPU %',
      },
    ],
    thresholds: [
      { value: 90, color: '#d32f2f' },
      { value: 70, color: '#ed6c02' },
    ],
  },
  {
    title: 'Memory Usage',
    group: 'Overview',
    chartType: 'stat',
    unit: 'percent',
    queries: [
      {
        expr: '100 - ((windows_os_physical_memory_free_bytes{instance=~"{{instance}}.*"} / windows_cs_physical_memory_bytes{instance=~"{{instance}}.*"}) * 100)',
        legend: 'Memory %',
      },
    ],
    thresholds: [
      { value: 90, color: '#d32f2f' },
      { value: 70, color: '#ed6c02' },
    ],
  },
  {
    title: 'Disk Usage C:',
    group: 'Overview',
    chartType: 'stat',
    unit: 'percent',
    queries: [
      {
        expr: '100 - ((windows_logical_disk_free_bytes{instance=~"{{instance}}.*",volume="C:"} / windows_logical_disk_size_bytes{instance=~"{{instance}}.*",volume="C:"}) * 100)',
        legend: 'Disk %',
      },
    ],
    thresholds: [
      { value: 90, color: '#d32f2f' },
      { value: 80, color: '#ed6c02' },
    ],
  },
  {
    title: 'Processor Queue',
    group: 'Overview',
    chartType: 'stat',
    unit: 'none',
    queries: [
      {
        expr: 'windows_system_processor_queue_length{instance=~"{{instance}}.*"}',
        legend: 'Queue',
      },
    ],
  },

  // --- Time Series ---
  {
    title: 'CPU Usage',
    group: 'CPU',
    chartType: 'area',
    unit: 'percent',
    queries: [
      {
        expr: '100 - (avg by(instance) (rate(windows_cpu_time_total{mode="idle",instance=~"{{instance}}.*"}[$__rate_interval])) * 100)',
        legend: 'CPU %',
      },
    ],
  },
  {
    title: 'Memory Usage',
    group: 'Memory',
    chartType: 'stacked-area',
    unit: 'bytes',
    queries: [
      {
        expr: 'windows_cs_physical_memory_bytes{instance=~"{{instance}}.*"} - windows_os_physical_memory_free_bytes{instance=~"{{instance}}.*"}',
        legend: 'Used',
      },
      {
        expr: 'windows_os_physical_memory_free_bytes{instance=~"{{instance}}.*"}',
        legend: 'Available',
      },
    ],
  },
  {
    title: 'Disk Usage',
    group: 'Disk',
    chartType: 'area',
    unit: 'percent',
    queries: [
      {
        expr: '100 - ((windows_logical_disk_free_bytes{instance=~"{{instance}}.*",volume!~"HarddiskVolume.*"} / windows_logical_disk_size_bytes{instance=~"{{instance}}.*",volume!~"HarddiskVolume.*"}) * 100)',
        legend: '{{volume}}',
      },
    ],
  },
  {
    title: 'Disk I/O',
    group: 'Disk',
    chartType: 'area',
    unit: 'bytes/s',
    queries: [
      {
        expr: 'rate(windows_logical_disk_read_bytes_total{instance=~"{{instance}}.*",volume="C:"}[$__rate_interval])',
        legend: 'Read',
      },
      {
        expr: 'rate(windows_logical_disk_write_bytes_total{instance=~"{{instance}}.*",volume="C:"}[$__rate_interval])',
        legend: 'Write',
      },
    ],
  },
  {
    title: 'Network Traffic',
    group: 'Network',
    chartType: 'area',
    unit: 'bytes/s',
    queries: [
      {
        expr: 'rate(windows_net_bytes_received_total{instance=~"{{instance}}.*"}[$__rate_interval])',
        legend: 'Receive',
      },
      {
        expr: 'rate(windows_net_bytes_sent_total{instance=~"{{instance}}.*"}[$__rate_interval])',
        legend: 'Transmit',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Process Exporter
// ---------------------------------------------------------------------------
const processExporterPanels: MetricPanelDef[] = [
  {
    title: 'Top CPU Processes',
    group: 'Processes',
    chartType: 'stacked-area',
    unit: 'percent',
    queries: [
      {
        expr: 'topk(5, rate(namedprocess_namegroup_cpu_seconds_total{instance=~"{{instance}}.*"}[$__rate_interval]) * 100)',
        legend: '{{groupname}}',
      },
    ],
  },
  {
    title: 'Top Memory Processes',
    group: 'Processes',
    chartType: 'stacked-area',
    unit: 'bytes',
    queries: [
      {
        expr: 'topk(5, namedprocess_namegroup_memory_bytes{instance=~"{{instance}}.*",memtype="resident"})',
        legend: '{{groupname}}',
      },
    ],
  },
  {
    title: 'Thread Count',
    group: 'Processes',
    chartType: 'area',
    unit: 'none',
    queries: [
      {
        expr: 'topk(5, namedprocess_namegroup_num_threads{instance=~"{{instance}}.*"})',
        legend: '{{groupname}}',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// SQL Exporter
// ---------------------------------------------------------------------------
const sqlExporterPanels: MetricPanelDef[] = [
  {
    title: 'Connections',
    group: 'Overview',
    chartType: 'stat',
    unit: 'none',
    queries: [
      {
        expr: 'mssql_connections{instance=~"{{instance}}.*"}',
        legend: 'Connections',
      },
    ],
  },
  {
    title: 'Batch Requests/s',
    group: 'Overview',
    chartType: 'stat',
    unit: 'ops/s',
    queries: [
      {
        expr: 'rate(mssql_batch_requests_total{instance=~"{{instance}}.*"}[$__rate_interval])',
        legend: 'Batch/s',
      },
    ],
  },
  {
    title: 'Buffer Cache Hit %',
    group: 'Overview',
    chartType: 'stat',
    unit: 'percent',
    queries: [
      {
        expr: 'mssql_buffer_cache_hit_ratio{instance=~"{{instance}}.*"}',
        legend: 'Cache Hit %',
      },
    ],
    thresholds: [
      { value: 99, color: '#2e7d32' },
      { value: 95, color: '#ed6c02' },
    ],
  },
  {
    title: 'Page Life Expectancy',
    group: 'Overview',
    chartType: 'stat',
    unit: 'seconds',
    queries: [
      {
        expr: 'mssql_page_life_expectancy_seconds{instance=~"{{instance}}.*"}',
        legend: 'PLE',
      },
    ],
  },
  {
    title: 'Connections',
    group: 'SQL Server',
    chartType: 'area',
    unit: 'none',
    queries: [
      {
        expr: 'mssql_connections{instance=~"{{instance}}.*"}',
        legend: 'Connections',
      },
    ],
  },
  {
    title: 'Batch Requests/s',
    group: 'SQL Server',
    chartType: 'area',
    unit: 'ops/s',
    queries: [
      {
        expr: 'rate(mssql_batch_requests_total{instance=~"{{instance}}.*"}[$__rate_interval])',
        legend: 'Batch/s',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// VMware ESXi Exporter
// ---------------------------------------------------------------------------
const vmwareEsxiPanels: MetricPanelDef[] = [
  {
    title: 'Host CPU Usage',
    group: 'Overview',
    chartType: 'stat',
    unit: 'percent',
    queries: [
      {
        expr: 'vmware_host_cpu_usage{instance=~"{{instance}}.*"} / vmware_host_cpu_max{instance=~"{{instance}}.*"} * 100',
        legend: 'CPU %',
      },
    ],
    thresholds: [
      { value: 90, color: '#d32f2f' },
      { value: 70, color: '#ed6c02' },
    ],
  },
  {
    title: 'Host Memory Usage',
    group: 'Overview',
    chartType: 'stat',
    unit: 'percent',
    queries: [
      {
        expr: 'vmware_host_memory_usage{instance=~"{{instance}}.*"} / vmware_host_memory_max{instance=~"{{instance}}.*"} * 100',
        legend: 'Memory %',
      },
    ],
    thresholds: [
      { value: 90, color: '#d32f2f' },
      { value: 70, color: '#ed6c02' },
    ],
  },
  {
    title: 'VM Count',
    group: 'Overview',
    chartType: 'stat',
    unit: 'none',
    queries: [
      {
        expr: 'count(vmware_vm_power_state{instance=~"{{instance}}.*"} == 1)',
        legend: 'VMs',
      },
    ],
  },
  {
    title: 'Host CPU',
    group: 'VMware',
    chartType: 'area',
    unit: 'percent',
    queries: [
      {
        expr: 'vmware_host_cpu_usage{instance=~"{{instance}}.*"} / vmware_host_cpu_max{instance=~"{{instance}}.*"} * 100',
        legend: '{{host_name}}',
      },
    ],
  },
  {
    title: 'Host Memory',
    group: 'VMware',
    chartType: 'area',
    unit: 'percent',
    queries: [
      {
        expr: 'vmware_host_memory_usage{instance=~"{{instance}}.*"} / vmware_host_memory_max{instance=~"{{instance}}.*"} * 100',
        legend: '{{host_name}}',
      },
    ],
  },
  {
    title: 'Datastore Usage',
    group: 'VMware',
    chartType: 'area',
    unit: 'percent',
    queries: [
      {
        expr: '(1 - (vmware_datastore_freespace_size{instance=~"{{instance}}.*"} / vmware_datastore_capacity_size{instance=~"{{instance}}.*"})) * 100',
        legend: '{{ds_name}}',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Registry: prometheusJob -> panels
// ---------------------------------------------------------------------------
export const METRIC_TEMPLATES: Record<string, MetricPanelDef[]> = {
  node_exporter: nodeExporterPanels,
  windows_exporter: windowsExporterPanels,
  process_exporter: processExporterPanels,
  sql_exporter: sqlExporterPanels,
  vmware_esxi: vmwareEsxiPanels,
  // cansense-push handled dynamically in the hook (detects OS from available metrics)
};

/**
 * Resolve templates for a given prometheus job.
 * For `cansense-push`, returns null — the hook will detect OS via instant query.
 */
export function getTemplatesForJob(job: string | undefined): MetricPanelDef[] | null {
  if (!job) return null;
  if (job === 'cansense-push') return null; // resolved at runtime
  return METRIC_TEMPLATES[job] ?? null;
}
